import { motion, useReducedMotion } from 'motion/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { type Clip } from '@air/api/types';
import { Badge } from '@air/primitive-badge';
import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

import { EditClipsTagsModalAgentScopeDropdown } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/EditClipsTagsModalAgentScopeDropdown';
import {
  WorkspaceAgentAirMark,
  type WorkspaceAgentAirMarkHandle,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/WorkspaceAgentAirMark';
import { agenticStartTaskButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentStartTaskButtonVariants';
import styles from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentStartTaskCta.module.css';
import { WORKSPACE_AGENT_HERO_SUGGEST_TAGS } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import { useLaunchWorkspaceAgentHeroSuggestion } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useLaunchWorkspaceAgentHeroSuggestion';

const PROMO_ENTER_DELAY_MS = 500;
const PROMO_ENTER_DURATION_S = 0.3;

export type EditClipsTagsModalAgentPromoProps = {
  clipIds: Clip['id'][];
  onCloseModal: () => void;
  onRemoveClipFromSelection: (clipId: Clip['id']) => void;
};

export const EditClipsTagsModalAgentPromo = memo(
  ({ clipIds, onCloseModal, onRemoveClipFromSelection }: EditClipsTagsModalAgentPromoProps) => {
    const shouldReduceMotion = !!useReducedMotion();
    const [isPromoVisible, setIsPromoVisible] = useState(false);
    const [isLaunchingHero, setIsLaunchingHero] = useState(false);
    const tagWithAirMarkRef = useRef<WorkspaceAgentAirMarkHandle>(null);
    const { launchHero } = useLaunchWorkspaceAgentHeroSuggestion();

    useEffect(() => {
      const delayMs = shouldReduceMotion ? 0 : PROMO_ENTER_DELAY_MS;
      const timer = window.setTimeout(() => setIsPromoVisible(true), delayMs);

      return () => window.clearTimeout(timer);
    }, [shouldReduceMotion]);

    const replayTagWithAirMark = useCallback(() => {
      if (shouldReduceMotion) {
        return;
      }

      tagWithAirMarkRef.current?.replay();
    }, [shouldReduceMotion]);

    const handleTagWithAir = useCallback(async () => {
      setIsLaunchingHero(true);

      try {
        onCloseModal();
        await launchHero(WORKSPACE_AGENT_HERO_SUGGEST_TAGS.id, {
          clipIds,
          source: 'edit_tags_modal',
        });
      } finally {
        setIsLaunchingHero(false);
      }
    }, [clipIds, launchHero, onCloseModal]);

    if (!isPromoVisible) {
      return null;
    }

    return (
      <motion.div
        animate={{ opacity: 1, scale: 1, y: 0 }}
        aria-live="polite"
        className="flex flex-col gap-2 rounded-6 bg-cyan-2 p-6"
        data-testid="EDIT_CLIPS_TAGS_AGENT_PROMO"
        initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.95, y: 8 }}
        transition={{ duration: shouldReduceMotion ? 0 : PROMO_ENTER_DURATION_S, ease: 'easeOut' }}
      >
        <Badge appearance="square" className="w-fit shrink-0 self-start" color="cyan" size="small">
          New at Air
        </Badge>
        <p className="text-14 text-gray-12 leading-5">
          <span className="font-semibold">Air can analyze the content and suggest tags for these items.</span> You can
          review the suggestions before anything is changed.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <EditClipsTagsModalAgentScopeDropdown
            clipIds={clipIds}
            onRemoveClipFromSelection={onRemoveClipFromSelection}
          />
          <div className={cn(styles.agenticBlueCta, 'inline-flex shrink-0 rounded-8')}>
            <Button
              appearance="filled"
              className={agenticStartTaskButton()}
              color="blue"
              data-testid="EDIT_CLIPS_TAGS_TAG_WITH_AIR_BUTTON"
              disabled={isLaunchingHero}
              onClick={handleTagWithAir}
              onFocus={replayTagWithAirMark}
              onPointerEnter={replayTagWithAirMark}
              size="medium"
            >
              <WorkspaceAgentAirMark autoplayOnMount ref={tagWithAirMarkRef} />
              Tag with Air
            </Button>
          </div>
        </div>
      </motion.div>
    );
  },
);

EditClipsTagsModalAgentPromo.displayName = 'EditClipsTagsModalAgentPromo';
